
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import isNumber from 'lodash/isNumber';
import { i as isPercent } from './table-auto-height-mixin.js';
import { ResizeObserver } from 'vue-resize';
import clamp from 'lodash/clamp';
import { g as getRelativeStrokeWidth, a as getRelativeThickness, i as isInProgress, b as isComplete, c as isNotStarted, d as getTrickleProgress, q as queue } from './utils-mixin.js';

var script = {
  name: 'PendoProgressBar',
  components: {
    ResizeObserver
  },
  props: {
    value: {
      type: Number,
      default: 0,
      required: true,
      validator: val => val >= 0 && val <= 100
    },
    height: {
      type: Number,
      default: 8
    },
    width: {
      type: [Number, String],
      default: 200
    },
    strokeWidth: {
      type: Number,
      default: 1
    },
    colors: {
      type: Object,
      default: () => ({
        default: {
          fill: '#EAECF1',
          stroke: '#EAECF1'
        },
        progress: {
          fill: '#128297',
          stroke: '#128297'
        },
        complete: {
          fill: '#00C583',
          stroke: '#00C583'
        }
      })
    },
    trickle: {
      type: Boolean,
      default: false
    },
    trickleSpeed: {
      type: Number,
      default: 200
    },
    easing: {
      type: String,
      default: 'ease-in-out'
    },
    speed: {
      type: Number,
      default: 200
    }
  },

  data() {
    return {
      computedWidth: parseInt(this.width, 10),
      internalValue: this.value,
      minInProgressValue: 2,
      maxInProgressValue: 99.4
    };
  },

  computed: {
    isPercentWidth() {
      return isPercent(this.width);
    },

    styleObj() {
      if (isNumber(this.width)) {
        return {
          width: `${this.width}px`
        };
      }

      return {
        width: this.width
      };
    },

    relativeStrokeWidth() {
      const strokeWidth = this.strokeWidth,
            computedWidth = this.computedWidth;
      return getRelativeStrokeWidth({
        strokeWidth,
        width: computedWidth
      });
    },

    relativeHeight() {
      const height = this.height,
            computedWidth = this.computedWidth;
      return getRelativeThickness({
        thickness: height,
        width: computedWidth
      });
    },

    isInProgress() {
      return isInProgress(this.internalValue);
    },

    isComplete() {
      return isComplete(this.internalValue);
    },

    isNotStarted() {
      return isNotStarted(this.internalValue);
    },

    progressWidth() {
      const strokeWidth = this.relativeStrokeWidth * 2;
      const progress = this.internalValue - strokeWidth;
      return clamp(progress, 0, 100);
    }

  },
  watch: {
    value() {
      if (!this.trickle) {
        this.internalValue = this.value;
        return;
      }

      const value = clamp(this.value, this.internalValue, 100);

      if (value === 100) {
        this.done();
      } else {
        this.increment(value);
      }
    }

  },

  async mounted() {
    await this.$nextTick();

    if (this.isPercentWidth) {
      this.setComputedWidth({
        width: this.$el.offsetWidth
      });
    }
  },

  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },

  methods: {
    setComputedWidth({
      width
    }) {
      requestAnimationFrame(() => {
        this.computedWidth = width;
      });
    },

    increment(amount) {
      if (this.isNotStarted) {
        this.start();
      }

      if (this.isComplete) {
        return;
      }

      if (typeof amount !== 'number') {
        amount = this.internalValue + getTrickleProgress(this.internalValue);
      }

      amount = clamp(amount, 0, this.maxInProgressValue);
      this.set(amount);
    },

    done(force) {
      if (!force && !this.internalValue) {
        return;
      }

      this.increment(30 + 50 * Math.random());
      this.set(100);
    },

    start() {
      if (this.isNotStarted) {
        this.set(0);
      }

      if (this.trickle) {
        this.timer = setInterval(() => {
          this.increment();

          if (this.isComplete) {
            clearInterval(this.timer);
          }
        }, this.trickleSpeed);
      }
    },

    set(value) {
      value = clamp(value, this.minInProgressValue, 100);
      queue(next => {
        if (value === 1) {
          setTimeout(() => {
            setTimeout(() => {
              this.internalValue = 100;
              next();
            }, this.speed);
          }, this.speed);
        } else {
          this.internalValue = value;
          setTimeout(next, this.speed);
        }
      });
    }

  }
};

const __vue_script__ = script;

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "pendo-progress-bar",
    class: {
      'is-complete': _vm.isComplete,
      'is-in-progress': _vm.isInProgress,
      'is-not-started': _vm.isNotStarted
    },
    style: _vm.styleObj,
    attrs: {
      "role": "progressbar",
      "aria-valuenow": _vm.internalValue,
      "aria-valuemin": "0",
      "aria-valuemax": "100"
    }
  }, [_c('svg', {
    attrs: {
      "viewBox": "0 0 100 " + (_vm.relativeHeight * 2 + _vm.relativeStrokeWidth * 2) / 2
    }
  }, [_c('rect', {
    ref: "background",
    attrs: {
      "width": 100 - _vm.relativeStrokeWidth * 2,
      "x": _vm.relativeStrokeWidth,
      "y": _vm.relativeStrokeWidth,
      "height": _vm.relativeHeight - _vm.relativeStrokeWidth,
      "stroke-width": _vm.relativeStrokeWidth,
      "stroke": _vm.colors.default.stroke,
      "fill": _vm.colors.default.fill,
      "rx": _vm.relativeHeight / 2,
      "ry": _vm.relativeHeight / 2
    }
  }), _vm._v(" "), _c('rect', {
    ref: "progress",
    staticClass: "pendo-progress-bar__progress",
    style: {
      transition: "all " + _vm.speed + "ms " + _vm.easing
    },
    attrs: {
      "x": _vm.relativeStrokeWidth,
      "y": _vm.relativeStrokeWidth,
      "width": _vm.progressWidth,
      "height": _vm.relativeHeight - _vm.relativeStrokeWidth,
      "stroke-width": _vm.relativeStrokeWidth,
      "stroke": _vm.colors[_vm.isComplete ? 'complete' : 'progress'].stroke,
      "fill": _vm.colors[_vm.isComplete ? 'complete' : 'progress'].fill,
      "rx": _vm.relativeHeight / 2,
      "ry": _vm.relativeHeight / 2
    }
  })]), _vm._v(" "), _vm.isPercentWidth ? _c('resize-observer', {
    on: {
      "notify": _vm.setComputedWidth
    }
  }) : _vm._e()], 1);
};

var __vue_staticRenderFns__ = [];
const __vue_inject_styles__ = undefined;
const __vue_scope_id__ = undefined;
const __vue_is_functional_template__ = false;

function __vue_normalize__(template, style, script, scope, functional, moduleIdentifier, createInjector, createInjectorSSR) {
  const component = (typeof script === 'function' ? script.options : script) || {};
  component.__file = "pendo-progress-bar.vue";

  if (!component.render) {
    component.render = template.render;
    component.staticRenderFns = template.staticRenderFns;
    component._compiled = true;
    if (functional) component.functional = true;
  }

  component._scopeId = scope;

  return component;
}

var pendoProgressBar = __vue_normalize__({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__);

export default pendoProgressBar;
